// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { DataCache } from '../../../../Commons/DataCache';
import { Resources } from '@shared/localizations/Resources';
import { AccountType } from '@shared/utils/cache/Accounts/Account.types';
import { AccountActivityGroupEnum } from '@shared/utils/Enums/AccountDetailsEnum';
import { RulesSet } from '@shared/utils/Rules/RulesSet';
import { BaseAccountGroupController } from '../BaseControllers/BaseAccountGroupController';
import { RowItemDataNumberArray, RowItemDataStringArray } from '../../../cache/VerticalPanel/AccountDetails/RowItemData';
import { AccountFeature } from '@shared/utils/Account/AccountFeature';
import { Account } from '../../../cache/Account';
import { VerticalPanelColoringTypes } from '../../VerticalPanelColoringTypes';
import { GROUP } from '@shared/utils/Rules/AccountRules';
export class AccountActivityGroupController extends BaseAccountGroupController {
    getGroupItemsTypes() {
        return [
            // AccountActivityGroupEnum.CurrentFundCapital,
            // AccountActivityGroupEnum.FundCapitalGain,
            AccountActivityGroupEnum.Profit,
            AccountActivityGroupEnum.ProfitNet,
            AccountActivityGroupEnum.Positions,
            AccountActivityGroupEnum.Orders,
            AccountActivityGroupEnum.TotalPositionsValue
            // AccountActivityGroupEnum.BlockedByPamm
        ];
    }
    getDynamicItems() {
        return [];
    }
    getItemValue(type) {
        const curAccount = this.Account;
        const assetBalance = this.AssetBalance;
        switch (type) {
            case AccountActivityGroupEnum.CurrentFundCapital:
                return new RowItemDataNumberArray(curAccount.CurrPammCapital);
            case AccountActivityGroupEnum.FundCapitalGain:
                return new RowItemDataNumberArray(0.0);
            case AccountActivityGroupEnum.Profit:
                return new RowItemDataNumberArray(curAccount.Profit);
            case AccountActivityGroupEnum.ProfitNet:
                return new RowItemDataNumberArray(curAccount.ProfitNet ? curAccount.ProfitNet : 0);
            case AccountActivityGroupEnum.Positions:
                return new RowItemDataNumberArray(curAccount.AccountType === AccountType.MultiAsset
                    ? assetBalance.OpenPosNumber
                    : curAccount.OpenPosNumber);
            case AccountActivityGroupEnum.Orders:
                return new RowItemDataNumberArray(curAccount.AccountType === AccountType.MultiAsset
                    ? assetBalance.OpenOrdNumber
                    : curAccount.OpenOrdNumber);
            case AccountActivityGroupEnum.BlockedByPamm:
                return new RowItemDataNumberArray(curAccount.StartPammCapital);
            case AccountActivityGroupEnum.TotalPositionsValue:
                return new RowItemDataNumberArray(Account.GetAccountFeature(AccountFeature.TotalPositionsValue, curAccount, assetBalance));
        }
    }
    getItemFormattedValue(type, key) {
        const curAccount = this.Account;
        const assetBalance = this.AssetBalance;
        const value = this.getItemValue(type);
        switch (type) {
            case AccountActivityGroupEnum.CurrentFundCapital:
                return new RowItemDataStringArray(assetBalance.formatPriceExactly(value.firstValue));
            case AccountActivityGroupEnum.FundCapitalGain:
                return new RowItemDataStringArray(assetBalance.formatPriceExactly(0));
            case AccountActivityGroupEnum.Profit:
                return new RowItemDataStringArray(assetBalance.formatPrice(curAccount.Profit));
            case AccountActivityGroupEnum.ProfitNet:
                return new RowItemDataStringArray(!isNaN(curAccount.ProfitNet)
                    ? assetBalance.formatPrice(curAccount.ProfitNet)
                    : Resources.getResource('general.Calculating'));
            case AccountActivityGroupEnum.Positions:
                return new RowItemDataStringArray(value.firstValue.toString());
            case AccountActivityGroupEnum.Orders:
                return new RowItemDataStringArray(value.firstValue.toString());
            case AccountActivityGroupEnum.BlockedByPamm:
                return new RowItemDataStringArray(assetBalance.formatPriceExactly(value.firstValue));
            case AccountActivityGroupEnum.TotalPositionsValue:
                return new RowItemDataStringArray(Account.GetAccountFeatureString(value.firstValue, AccountFeature.TotalPositionsValue, curAccount, assetBalance));
        }
    }
    getItemNameKey(type) {
        switch (type) {
            case AccountActivityGroupEnum.CurrentFundCapital:
                return 'panel.accounts.CurrentFundCapital';
            case AccountActivityGroupEnum.FundCapitalGain:
                return 'panel.accounts.FundCapitalGain';
            case AccountActivityGroupEnum.Profit:
                return 'panel.accounts.Profit';
            case AccountActivityGroupEnum.ProfitNet:
                return 'panel.accounts.ProfitNet';
            case AccountActivityGroupEnum.Positions:
                return 'panel.accounts.Positions';
            case AccountActivityGroupEnum.Orders:
                return 'panel.accounts.Orders';
            case AccountActivityGroupEnum.BlockedByPamm:
                return 'panel.accounts.BlockedByPamm';
            case AccountActivityGroupEnum.TotalPositionsValue:
                return 'panel.accounts.TotalPositionsValue';
        }
    }
    getItemRule(type) {
        switch (type) {
            case AccountActivityGroupEnum.CurrentFundCapital:
                return 'FUNCTION_ACCOUNT_DETAILS_BALANCE';
            case AccountActivityGroupEnum.FundCapitalGain:
                return 'FUNCTION_ACCOUNT_DETAILS_PROJECTED_BALANCE';
            case AccountActivityGroupEnum.Profit:
                return 'FUNCTION_ACCOUNT_DETAILS_OPEN_GROSS_PNL';
            case AccountActivityGroupEnum.ProfitNet:
                return 'FUNCTION_ACCOUNT_DETAILS_OPEN_NET_PNL';
            case AccountActivityGroupEnum.Positions:
                return 'FUNCTION_ACCOUNT_DETAILS_POSITIONS';
            case AccountActivityGroupEnum.Orders:
                return 'FUNCTION_ACCOUNT_DETAILS_ORDERS';
            case AccountActivityGroupEnum.BlockedByPamm:
                return 'FUNCTION_ACCOUNT_DETAILS_BLOCKED_BALANCE';
            case AccountActivityGroupEnum.TotalPositionsValue:
                return 'FUNCTION_TOTAL_POSITIONS_VALUE';
            default:
                return null;
        }
    }
    getItemGroupRule() {
        return GROUP.ACCOUNT_ACTIVITY;
    }
    isHiddenItemRow(type) {
        if (super.isHiddenItemRow(type)) {
            return true;
        }
        if (DataCache.isAllowedForMyUser(RulesSet.FUNCTION_EXT_RISK)) {
            return true;
        }
    }
    getColoringMethod(type) {
        return VerticalPanelColoringTypes.Default;
    }
}
